import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import {
  BUDGET_SORT_OPTIONS,
  type BudgetSortOption,
} from "../../helpers/budgetSort";
// ─────────────────────────────────────────────────────────────────────────────

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

interface BudgetControlCardSortByProps {
  sortKey: string;
  sortDir: "asc" | "desc";
  onApply: (key: string, dir: "asc" | "desc") => void;
  // Values that are selectable; anything not here renders disabled (graph view)
  enabledValues?: Set<string>;
  options?: BudgetSortOption[];
}

export default function BudgetControlCardSortBy({
  sortKey,
  sortDir,
  onApply,
  enabledValues,
  options = BUDGET_SORT_OPTIONS,
}: BudgetControlCardSortByProps) {
  const [open, setOpen] = React.useState(false);
  const [pendingKey, setPendingKey] = React.useState(sortKey);
  const [pendingDir, setPendingDir] = React.useState<"asc" | "desc">(sortDir);

  const isEnabled = (value: string) =>
    !enabledValues || enabledValues.has(value);

  const toggleDrawer = (newOpen: boolean) => () => {
    if (newOpen) {
      setPendingKey(sortKey);
      setPendingDir(sortDir);
    }
    setOpen(newOpen);
  };

  const handleToggle = (value: string) => {
    if (!isEnabled(value)) return;
    if (pendingKey === value) {
      setPendingDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setPendingKey(value);
      setPendingDir("asc");
    }
  };

  const handleApplySorting = () => {
    onApply?.(pendingKey, pendingDir);
    setOpen(false);
  };

  const currentLabel =
    options.find((o) => o.value === sortKey)?.label ?? "Brand Name";

  const ShortList = (
    <Box role="shortBy">
      <Puller />
      <Stack justifyContent="end" direction="row" p={1}>
        <IconButton
          aria-label="Close"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        Sort By
      </Typography>

      <Box sx={{ my: 2 }}>
        {options.map(({ label, value }) => {
          const isCurrentSort = pendingKey === value;
          const enabled = isEnabled(value);

          return (
            <Button
              key={value}
              disabled={!enabled}
              onClick={() => handleToggle(value)}
              startIcon={
                isCurrentSort && enabled ? (
                  <ArrowUpwardIcon
                    sx={{
                      position: "absolute",
                      left: "7px",
                      top: "7px",
                      transform:
                        pendingDir === "desc"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "0.2s",
                      width: spacing.Spacing.lg.$value,
                    }}
                  />
                ) : (
                  <ArrowUpwardIcon
                    sx={{
                      position: "absolute",
                      left: "7px",
                      top: "7px",
                      opacity: 0,
                      width: spacing.Spacing.lg.$value,
                    }}
                  />
                )
              }
              variant="outlined"
              color="secondary"
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: typography.Size.Text.sm.$value + "!important",
                },
                borderRadius: 2,
                mr: spacing.Spacing.sm.$value,
                mb: spacing.Spacing.sm.$value,
                py: spacing.Spacing.xs.$value,
                paddingRight: spacing.Spacing["1xl"].$value,
                paddingLeft: spacing.Spacing.xl.$value,
                textTransform: "capitalize",
                fontWeight: typography.Font_weight.Regular.$value,
                border: "1px solid " + colors.GrayLightMode[200].$value,
                color: colors.GrayLightMode[700].$value,
                fontSize: typography.Size.Text.xs.$value,
                ...(isCurrentSort &&
                  enabled && {
                    border:
                      "1px solid " + colors.Brand[600].$value + "!important",
                    boxShadow: "none !important",
                    color: colors.GrayLightMode[900].$value,
                  }),
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      <Button
        data-tracker="827"
        fullWidth
        variant="contained"
        sx={{
          textTransform: "capitalize",
          boxShadow: "none !important",
          background: colors.Brand[600].$value,
          borderRadius: 2,
        }}
        onClick={handleApplySorting}
      >
        Apply
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        data-tracker="826"
        onClick={toggleDrawer(true)}
        component="span"
        display={"flex"}
        alignItems={"center"}
        gap={0.5}
        sx={{
          fontWeight: typography.Font_weight.Medium.$value,
          color: colors.Brand[700].$value,
          cursor: "pointer",
        }}
      >
        {currentLabel}{" "}
        <ArrowUpwardIcon
          sx={{
            color: colors.Brand[700].$value,
            fontSize: typography.Size.Text.xl.$value,
            transform: sortDir === "desc" ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.2s",
          }}
        />
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {ShortList}
      </Drawer>
    </>
  );
}
